<template>
  <div class="register-calendar-panel" v-loading="loading">
    <div class="calendar-container">
      <el-calendar v-model="calendarCursor">
        <template #date-cell="{ data }">
          <div class="date-cell">
            <div class="date-label" :class="{ 'is-other-month': data.isSelected === false && !isSameMonth(data.day, calendarCursor) }">
              {{ formatDayNumber(data.day) }}
            </div>
            <div class="counts">
              <div class="count total">{{ getCount(data.day) }}</div>
            </div>
          </div>
        </template>
      </el-calendar>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { getRegisterCalendar } from '@/services/manager';

export default {
  name: 'RegisterCalendarPanel',
  setup() {
    const loading = ref(false);
    const itemsMap = ref({});

    const today = new Date();
    const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const calendarCursor = ref(new Date(defaultStart));

    const handleQuery = async () => {
      loading.value = true;
      try {
        const year = calendarCursor.value.getFullYear();
        const month = calendarCursor.value.getMonth();
        const startDate = formatDate(new Date(year, month, 1));
        const endDate = formatDate(new Date(year, month + 1, 0));

        const res = await getRegisterCalendar({ startDate, endDate });
        if (!res?.success) throw new Error(res?.message || '조회 실패');

        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        const map = {};

        for (const it of items) {
          map[it.date] = {
            total: it.total || 0
          };
        }

        itemsMap.value = map;
      } catch (error) {
        console.error(error);
        ElMessage.error(error?.response?.data?.message || error.message || '데이터 조회 중 오류가 발생했습니다.');
        itemsMap.value = {};
      } finally {
        loading.value = false;
      }
    };

    const getCount = day => {
      const d = toDateString(day);
      return itemsMap.value[d]?.total ?? 0;
    };

    const isSameMonth = (day, cursorYmd) => {
      const d = new Date(day);
      const c = new Date(cursorYmd);
      return d.getFullYear() === c.getFullYear() && d.getMonth() === c.getMonth();
    };

    watch(calendarCursor, () => {
      handleQuery();
    });

    onMounted(() => {
      handleQuery();
    });

    return {
      loading,
      calendarCursor,
      getCount,
      isSameMonth,
      formatDayNumber
    };
  }
};

function pad2(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toDateString(isoOrDate) {
  if (!isoOrDate) return '';
  if (typeof isoOrDate === 'string') return isoOrDate.substring(0, 10);
  return formatDate(new Date(isoOrDate));
}

function formatDayNumber(iso) {
  const d = new Date(iso);
  return d.getDate();
}
</script>

<style scoped>
.register-calendar-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.calendar-container {
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  border: 1px solid var(--el-border-color-lighter);
  overflow: auto;
}

.date-cell {
  min-height: 120px;
  padding: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.date-label.is-other-month {
  opacity: 0.5;
}

.counts {
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
}

.count {
  font-size: 12px;
  padding: 4px 6px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid var(--el-border-color-extra-light);
}

.count.total {
  background: #e8f5e9;
  color: #2e7d32;
}
</style>
